/**
 * Cookie / tracking consent banner.
 *
 * Renders only while the visitor is undecided. Both choices are a single click
 * and given equal visual weight — a banner where refusing is harder than
 * accepting is not consent, and an advertising pixel is exactly the case where
 * that distinction is load-bearing.
 *
 * Decision is stored by `@/lib/consent`, which every analytics tag checks
 * before initialising.
 */

import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { getConsent, setConsent, onConsentChange } from '@/lib/consent';

export function ConsentBanner(): JSX.Element | null {
  const [isUndecided, setIsUndecided] = useState(false);

  useEffect(() => {
    // Read in an effect, not during render: localStorage is unavailable during
    // SSR/prerender and would throw before the app ever mounts.
    setIsUndecided(getConsent() === 'unset');
    return onConsentChange((state) => setIsUndecided(state === 'unset'));
  }, []);

  if (!isUndecided) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie preferences"
      data-testid="consent-banner"
      className="fixed bottom-0 inset-x-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
    >
      <div className="container mx-auto px-4 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <p className="text-sm text-muted-foreground flex-1">
          We use cookies to understand how the site is used and to measure our
          advertising. You can decline and everything still works.{' '}
          <Link href="/privacy-policy" className="underline underline-offset-2">
            Privacy policy
          </Link>
          .
        </p>
        <div className="flex gap-2 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConsent('denied')}
            data-testid="consent-decline"
          >
            Decline
          </Button>
          <Button
            size="sm"
            onClick={() => setConsent('granted')}
            data-testid="consent-accept"
          >
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ConsentBanner;
